/* eslint-disable max-lines */
/* eslint-disable no-nested-ternary */
import useProfile from '@api/hooks/profile/useProfile';
import Image from '@base/FutureImage';
import styled from '@emotion/styled';
import { Fonts, Spacings, mediaQuery } from '@styles/index';
import { Typography } from 'aquilla/core/atoms/Typography';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import User from '@models/User';
import dateHelper from '@utils/dates';
import { EDUCATOR_TYPES } from '@cont/Batch/utils';
import { ExEducatorDescription } from '@constants/index';
import {
  EDUCATOR_FOLLOWED,
  EDUCATOR_PROFILE,
  EDUCATOR_UNFOLLOWED
} from '@constants/segment';
import { EDUCATOR_UID, EDUCATOR_USERNAME } from '@events/segmentProps';
import { followEducator, unFollowEducator } from '@api/fetchers/class/updaters';
import { segment } from '@actions/analytics';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getMeUsername } from '@api/selectors/auth';
import { useSnackbar } from '@stores/snackbar';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useEducatorDetailsSegmentData from '@hooks/useEducatorDetailsSegmentData';
import useOneLinkSmartScriptV2 from '@hooks/useOneLinkSmartScriptV2';
import { EDUCATOR_PAGE } from '@constants/appFlyerLinks';
import Button from 'aquilla/core/molecules/Button';
import { useRouter } from 'next/router';
import { RightChevron } from 'aquilla/icons/actions/RightChevron';
import segmentStorage from '@events/segmentStorage';
import deviceHelper from '@utils/deviceHelper';
import useEducatorFollowers from '@api/hooks/educator/useEducatorFollowers';
import EducatorCommunityModal from '../EducatorCommunityModal';
import EducatorStatsNew from '../EducatorStatsNew';
import { TabWrapperID } from '../constants';

const HeaderWrapper = styled.div`
  position: sticky;
  top: 42px;
  background-color: var(--color-base-1);
  z-index: 2;

  ${mediaQuery.uptoTablet} {
    position: relative;
    top: 0;
  }
`;
const HeaderContainer = styled.div`
  width: 100%;
  position: sticky;
  top: ${Spacings.SPACING_18B};
  z-index: 1;

  ${mediaQuery.uptoTablet} {
    position: relative;
    top: 0px;
  }

  /* ${mediaQuery.uptoMobile} {
    background-size: contain;
  } */
`;

const EducatorDetailContainer = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  padding: ${Spacings.SPACING_8B} 0 ${Spacings.SPACING_4B};
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: 0 auto;

  ${mediaQuery.uptoTablet} {
    flex-direction: column;
    position: static;
    width: 100%;
    padding: ${Spacings.SPACING_9B} ${Spacings.SPACING_4B} 0;
  }
`;

const EducatorDetails = styled.div`
  position: relative;
  padding: ${Spacings.SPACING_14} 0 0 170px;
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_2B};
  transition: all linear 200ms;
  &.hide {
    padding-left: 0;
  }

  ${mediaQuery.uptoTablet} {
    max-width: 100%;
    align-items: center;
    padding: ${Spacings.SPACING_2B} 0 ${Spacings.SPACING_14};
  }
`;

const EducatorStats = styled.div`
  position: sticky;
  top: 204px;
  overflow: hidden;
  height: ${Spacings.SPACING_15B};
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: 0 auto;
  padding: ${Spacings.SPACING_5B} 0 0 170px;
  transition: width linear 200ms, opacity linear 200ms, display linear 0ms 200ms;
  &.hide {
    height: 0px;
    opacity: 0;
    display: none;
  }
  ${mediaQuery.uptoTablet} {
    height: unset;
    position: static;
    width: 100%;
    padding: ${Spacings.SPACING_5B} 0 0;
  }
`;

const ThumbnailWrapper = styled.div`
  width: 146px;
  position: absolute;
  transition: all linear 200ms;

  background: var(--color-educator-bg);
  border-radius: 8px;
  border: 4.28px white solid;
  aspect-ratio: 146 / 168;
  overflow: hidden;
  &.hide {
    width: 0px;
    opacity: 0px;
    border: 0px white solid;
  }

  > img {
    width: 100%;
    height: 100%;
    position: absolute;
  }

  ${mediaQuery.uptoTablet} {
    position: relative;
    width: 105px;
    margin: 0 auto;
  }
`;

const EducatorImageWrapper = styled.div`
  position: relative;
  top: 4px;
  left: 50%;
  transform: translateX(-50%);
  height: auto;
  width: 100%;

  img {
    width: 100%;
    height: auto;
    transform: none !important;
    object-fit: ${({ $isNew }) => ($isNew ? 'contain' : 'cover')};
  }
`;

const EducatorBioWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoTablet} {
    flex-direction: column;
    align-items: center;
    gap: ${Spacings.SPACING_0B};
    > :nth-child(2) {
      display: none;
    }
  }
`;

const EducatorBioContainer = styled.div``;

const BioWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  ${mediaQuery.uptoTablet} {
    flex-direction: column;
    align-items: center;
    > :nth-child(2) {
      display: none;
    }
  }
`;

const EducatorBio = styled(Typography)`
  overflow: hidden;
  text-overflow: clip;
  display: -webkit-box;
  -webkit-line-clamp: 1; /* number of lines to show */
  line-clamp: 1;
  -webkit-box-orient: vertical;
  max-width: ${({ $isExEducator }) => ($isExEducator ? 'unset' : '627px')};

  ${mediaQuery.uptoTablet} {
    -webkit-line-clamp: 2;
    text-align: center;
    font-size: ${Fonts.ULTRA_SMALL};
    width: 100%;
  }
`;

const ChevronWrapper = styled.div`
  display: ${({ $isExEducator }) => ($isExEducator ? 'none' : 'flex')};
  margin-left: -${Spacings.SPACING_3B};
  cursor: pointer;
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const EducatorName = styled(Typography)`
  ${mediaQuery.uptoTablet} {
    font-size: 20px;
    line-height: 24px;
    margin: ${Spacings.SPACING_2B} 0 0;
  }
`;

const FollowButton = styled(Button)`
  align-self: center;
  min-width: 176px;
`;

const EducatorDesignationWrapper = styled.div`
  &.web {
    display: block;
    ${mediaQuery.uptoTablet} {
      display: none;
    }
  }
  &.mWeb {
    display: none;
    ${mediaQuery.uptoTablet} {
      display: block;
    }
  }
`;

// eslint-disable-next-line react/display-name
const EducatorCardThumbnail = forwardRef(
  ({ img, isNew = false, backgroundImage }, ref) => {
    return (
      <ThumbnailWrapper ref={ref}>
        <Image src={backgroundImage} width={146} height={167.66} alt="icon" />
        <EducatorImageWrapper $isNew={isNew}>
          <Image alt="class" src={img} width={128} height={170} />
        </EducatorImageWrapper>
      </ThumbnailWrapper>
    );
  }
);

const EducatorHeader = ({ username }) => {
  const router = useRouter();
  const {
    query: { authorID }
  } = router;
  const selectedGoalUID = useSelectedGoalUID();
  const { followedEducators = [], refetchFollowedEducator } =
    useEducatorFollowers();
  const { open: showSnackbar } = useSnackbar();
  const { profileData: educator } = useProfile(username, {
    goalUid: selectedGoalUID,
    revalidateOnMount: false
  });
  const {
    dateJoined,
    educatorTypeV2,
    isExEducator,
    isEducator,
    avatarV2,
    oldAvatar,
    avatar,
    isPlatformEducator,
    designation
  } = educator;

  const isFollowing = !!followedEducators.find(
    (followedEducator) => followedEducator.username === username
  );

  const educatorImage = avatarV2 || oldAvatar || avatar;
  const isNew = educatorImage === avatarV2;
  const isPartnerEducator = EDUCATOR_TYPES.PARTNER === educatorTypeV2;

  const { bio, firstName, lastName } = educator;
  const isPlatinumEducator = !!(
    educator?.isPlatformEducator || educator?.isPlatinumEducator
  );
  const isMobile = deviceHelper.isMobileDevice();
  const intersectionRatio = useRef(1);
  const thumbnailRef = useRef(null);
  const educatorDetail = useRef(null);
  const educatorStat = useRef(null);
  const [showHeaderFollowButton, setButtonState] = useState(false);
  const followClicked = useRef(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const myUsername = useMyInfo(getMeUsername);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const segmentData = useEducatorDetailsSegmentData({
    educatorProfile: educator
  });
  const appDownloadLink = useOneLinkSmartScriptV2({
    campaign: EDUCATOR_PAGE
  });
  const onCommunityModalClose = () => {
    setIsModalOpen(false);
  };
  const handleAnimation = (entry) => {
    let scrollDirection = 'down';
    if (intersectionRatio.current > entry.intersectionRatio)
      scrollDirection = 'up';
    intersectionRatio.current = entry.intersectionRatio;
    let tabsTopPadding = window
      .getComputedStyle(document.getElementById(TabWrapperID))
      ?.padding?.split(' ')[0]
      ?.replace('px', '');
    tabsTopPadding = parseInt(tabsTopPadding, 10);
    if (scrollDirection === 'up') {
      thumbnailRef.current.classList.add('hide');
      educatorDetail.current.classList.add('hide');
      thumbnailRef.current.parentElement.classList.add('hide');
      if (educatorStat.current) {
        educatorStat.current.classList.add('hide');
      }
      window.scrollBy(0, tabsTopPadding);
      setButtonState(true);
    } else if (scrollDirection === 'down' && entry.intersectionRatio > 0.3) {
      thumbnailRef.current.classList.remove('hide');
      educatorDetail.current.classList.remove('hide');
      thumbnailRef.current.parentElement.classList.remove('hide');
      if (educatorStat.current) {
        educatorStat.current.classList.remove('hide');
      }
      setButtonState(false);
    }
  };
  const callback = (entries) => {
    entries.forEach((entry) => {
      handleAnimation(entry);
    });
  };

  useEffect(() => {
    let tempObserver;
    if (!isMobile) {
      const layoutHeaderHeight =
        document.getElementsByTagName('header')[0].clientHeight;
      const educatorHeaderHeight = document.querySelector(
        'div[class*=HeaderWrapper]'
      ).clientHeight;
      const marginTop = layoutHeaderHeight + educatorHeaderHeight;
      const options = {
        rootMargin: `-${marginTop}px 0px 0px 0px`,
        threshold: [
          0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7,
          0.75, 0.8, 0.85, 0.9, 0.95, 1
        ]
      };
      tempObserver = new IntersectionObserver(callback, options);
      const target = document.querySelector(`#${TabWrapperID}`);
      if (target) tempObserver.observe(target);
    }

    return () => {
      if (tempObserver) tempObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const onClickFollow = async (user, follow, userUID = null) => {
    const eventName = follow ? EDUCATOR_FOLLOWED : EDUCATOR_UNFOLLOWED;
    const newSegmentData = {};
    if (user) newSegmentData[EDUCATOR_USERNAME] = user;
    if (userUID) newSegmentData[EDUCATOR_UID] = userUID;
    newSegmentData.last_primary_source = EDUCATOR_PROFILE;

    const toggleEducatorFollow = isFollowing
      ? unFollowEducator
      : followEducator;
    const res = await toggleEducatorFollow({ userName: username });
    if (res.error) {
      showSnackbar({
        message: res.error.message,
        type: 'error'
      });
      return;
    }
    refetchFollowedEducator();
    if (!isFollowing) setIsModalOpen(true);
    segment.track(eventName, { ...segmentData, ...newSegmentData });
  };

  useEffect(() => {
    if (isLoggedIn && followClicked.current) {
      onClickFollow(username, !isFollowing);
      followClicked.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const handleOnClickFollow = () => {
    if (isMobile) {
      window.open(appDownloadLink, '_self');
      return;
    }
    if (!isLoggedIn) {
      segmentStorage.setItem('lpss', 'Follow');
      followClicked.current = true;
      openLoginDrawer();
      return;
    }
    onClickFollow(username, !isFollowing);
  };
  const dateOfJoiningString = dateJoined
    ? `Joined Unacademy in ${dateHelper.getMonthString(
        dateJoined
      )} ${dateHelper.getYear(dateJoined)}`
    : null;

  const handleBioClick = () => {
    if (isExEducator) return;
    segment.track('Educator Bio Clicked', {
      last_primary_source_section: 'Educator Header'
    });
    const href = '/[authorID]/about';
    const as = `/${authorID}/about`;
    if (router.pathname !== href) router.push(href, as);
  };

  return (
    <HeaderWrapper>
      <HeaderContainer>
        <EducatorDetailContainer>
          <EducatorCardThumbnail
            img={educatorImage}
            isNew={isNew}
            ref={thumbnailRef}
            backgroundImage="EducatorBG.svg"
          />
          <EducatorDetails ref={educatorDetail}>
            {designation && (
              <EducatorDesignationWrapper className="mWeb">
                <Typography variant="h6" color="#27B772">
                  {designation}
                </Typography>
              </EducatorDesignationWrapper>
            )}
            <EducatorName
              as="h1"
              variant="h2"
            >{`${firstName} ${lastName}`}</EducatorName>
            <EducatorBioWrapper>
              {designation && (
                <>
                  <EducatorDesignationWrapper className="web">
                    <Typography variant="h6" color="#27B772">
                      Sr Facult, chemistry
                    </Typography>
                  </EducatorDesignationWrapper>
                  <Typography variant="h6" color="var(--color-text-secondary)">
                    •
                  </Typography>
                </>
              )}
              <BioWrapper>
                <EducatorBioContainer onClick={handleBioClick}>
                  <EducatorBio
                    $isExEducator={isExEducator}
                    variant="p2"
                    color="var(--color-text-primary)"
                  >
                    {isEducator
                      ? bio
                      : isExEducator
                      ? ExEducatorDescription
                      : dateOfJoiningString}
                  </EducatorBio>
                </EducatorBioContainer>
                {bio && (
                  <ChevronWrapper
                    onClick={handleBioClick}
                    $isExEducator={isExEducator}
                  >
                    <RightChevron width={16} height={16} />
                  </ChevronWrapper>
                )}
              </BioWrapper>
            </EducatorBioWrapper>
          </EducatorDetails>
          {showHeaderFollowButton &&
            !isPlatinumEducator &&
            myUsername !== username?.toLowerCase() &&
            !isExEducator && (
              <FollowButton
                onClick={handleOnClickFollow}
                variant={isFollowing ? 'secondary' : 'primary'}
              >
                {isFollowing ? 'Following' : 'Follow'}
              </FollowButton>
            )}
        </EducatorDetailContainer>
      </HeaderContainer>
      <EducatorStats
        ref={educatorStat}
        $isPlatformEducator={isPlatformEducator}
      >
        {!User.isLegendEducator(educator) && !isPlatinumEducator && (
          <EducatorStatsNew
            username={username}
            isEducator={educator.isEducator || isPartnerEducator}
            isExEducator={educator.isExEducator}
            isPlatinumEducator={isPlatinumEducator}
            onFollowClick={handleOnClickFollow}
          />
        )}
      </EducatorStats>
      <EducatorCommunityModal
        showModal={isModalOpen}
        handleCommunityModalClose={onCommunityModalClose}
        showLogo
      />
    </HeaderWrapper>
  );
};

export default EducatorHeader;
